import { z } from "zod";
import { EMPTY_ADDRESS, formatAddress } from "./address.js";
import { FAMILIES, familyOf } from "./business-type.js";
import { clientFaceOf } from "./client-face.js";
import { COUNTRIES } from "./locale.js";
import { addDays, dayOf, instantOf, isoWeekdayOf } from "./period.js";
import { withPlaceAccents } from "./place-accents.js";
import { natureOf, sells } from "./item-nature.js";
import { foldForSearch } from "./search.js";
import { upperMedian } from "./median.js";
import { NO_PROFILE_REVIEWS } from "./review.js";
import { NO_TRAJECTORY, TRAJECTORY_EXPLANATION, TRAJECTORY_TITLE } from "./trajectory.js";
import { wordsFor } from "./type-words.js";
import { isBusinessType, keepsLogbook, verticalOf } from "./verticals.js";
/**
 * THE BUSINESS'S PUBLIC PROFILE (spec 2026-10-20 §5): what `/t/<slug>` says
 * about it beyond its shop. The signals are VozQI's word, not the owner's:
 * computed from the operation on every read, shown only once they favour the
 * business, and only ever as a tier -- an exact count would tell a stranger
 * how much it sells.
 */
/** Every signal, in the order the page and the settings list them. */
export const SIGNAL_IDS = ["jobs", "response", "invoices", "clients", "returning", "trajectory"];
/** What the owner may switch off: hidden_signals' check, verbatim (0134). */
export const HIDEABLE_SIGNAL_IDS = ["jobs", "response", "invoices", "clients", "returning"];
/** Born off: shown only once the owner switches it on (shown_signals, 0134). */
export const BORN_OFF_SIGNAL_IDS = ["trajectory"];
/** Every hideable signal on, every born-off one off. */
export const NO_SWITCHES = { hidden: [], shown: [] };
export const JOB_TIERS = [10, 50, 100, 200, 500, 1000];
export const CLIENT_TIERS = [5, 10, 50, 100, 200, 500, 1000];
/** Fewer counted requests than this say nothing about how fast it answers. */
const RESPONSE_MIN_REQUESTS = 5;
export const RESPONSE_WINDOW_DAYS = 90;
export const JOBS_WINDOW_MONTHS = 12;
export const RETURNING_WINDOW_MONTHS = 24;
const RETURNING_MIN_CLIENTS = 10;
export const MAX_ABOUT_LENGTH = 300;
/** Monday to Saturday, 08:00 to 20:00: the hours a business that gave none is read in. */
export const DEFAULT_ATTENDED_HOURS = [1, 2, 3, 4, 5, 6].map((weekday) => ({
    weekday,
    opens: "08:00",
    closes: "20:00",
}));
/** The facts of a business with nothing to show: in its sample. */
export const NO_SIGNAL_FACTS = {
    jobs: 0, requests: [], invoicing: false, clients: 0, returning: { clients: 0, returning: 0 }, trajectory: NO_TRAJECTORY, reviews: NO_PROFILE_REVIEWS,
};
const PIN_SCALE = 1e6;
const toPinScale = (degrees) => Math.round(degrees * PIN_SCALE) / PIN_SCALE;
/** What a point off the map, or not a point, answers: the owner places it again. */
const PIN_PROBLEM_MESSAGE = "Ese punto no está en el mapa. Vuelve a ubicarlo.";
const degrees = (limit) => z.number({ error: PIN_PROBLEM_MESSAGE }).min(-limit, { error: PIN_PROBLEM_MESSAGE }).max(limit, { error: PIN_PROBLEM_MESSAGE }).transform(toPinScale);
export const profilePinSchema = z.strictObject({ lat: degrees(90), lng: degrees(180) }, { error: PIN_PROBLEM_MESSAGE });
/** What saving the settings of a profile never published answers: they hang off its row. */
export const PROFILE_NOT_PUBLISHED_MESSAGE = "Publica tu perfil primero.";
/**
 * The largest tier the count goes past, or null below the lowest. Past, not
 * reached: at exactly 10 the phrase would have to be the exact number, and
 * spec §5.1 never gives one.
 */
function tierOf(n, tiers) {
    let tier = null;
    for (const t of tiers)
        if (n > t)
            tier = t;
    return tier;
}
/** «Más de 10 trabajos terminados», or null below the lowest tier. */
export function tierLabel(n, tiers, noun) {
    const tier = tierOf(n, tiers);
    return tier === null ? null : `Más de ${String(tier)} ${noun}`;
}
/**
 * The instant a wall-clock time of `day` names in `tz`, in ms; "24:00" is the
 * next midnight. Through `instantOf`, so the offset is the one at that wall
 * time: on a changeover day an opening at 04:00 is still 04:00.
 */
function instantMs(day, time, tz) {
    return time === "24:00" ? Date.parse(instantOf(addDays(day, 1), "00:00", tz)) : Date.parse(instantOf(day, time, tz));
}
/**
 * The minutes between two instants that fall inside the business's hours, on
 * its own clock: a request at 22:00 answered at 08:00 waited none of them.
 * Walks the days in the zone and adds each range's overlap. Hours never given
 * read as `DEFAULT_ATTENDED_HOURS`.
 */
export function attendedMinutes(from, to, hours, timezone) {
    if (to.getTime() <= from.getTime())
        return 0;
    const week = hours.length === 0 ? DEFAULT_ATTENDED_HOURS : hours;
    const last = dayOf(to.toISOString(), timezone);
    let total = 0;
    for (let day = dayOf(from.toISOString(), timezone); day <= last; day = addDays(day, 1)) {
        const weekday = isoWeekdayOf(day);
        for (const range of week) {
            if (range.weekday !== weekday)
                continue;
            const start = Math.max(instantMs(day, range.opens, timezone), from.getTime());
            const end = Math.min(instantMs(day, range.closes, timezone), to.getTime());
            if (end > start)
                total += (end - start) / 60_000;
        }
    }
    return total;
}
/** Up to how many attended minutes each phrase holds; slower than the last, no phrase. */
const RESPONSE_STEPS = [
    { upTo: 60, label: "Responde en menos de 1 hora" },
    { upTo: 180, label: "Responde en menos de 3 horas" },
    { upTo: 480, label: "Responde el mismo día" },
];
const SAME_DAY_MINUTES = 480;
/**
 * The attended minutes each request counts with: an answered one, what it
 * waited; an open one, only once it has waited past a day -- it is already
 * slow, and leaving it out would flatter whoever never answers. A younger
 * open one may still be answered fast, so it does not count yet.
 */
function countedWaits(requests, ctx) {
    const waits = [];
    for (const r of requests) {
        const asked = new Date(r.askedAt);
        if (r.answeredAt !== null) {
            waits.push(attendedMinutes(asked, new Date(r.answeredAt), ctx.hours, ctx.timezone));
            continue;
        }
        const waited = attendedMinutes(asked, ctx.now, ctx.hours, ctx.timezone);
        if (waited > SAME_DAY_MINUTES)
            waits.push(waited);
    }
    return waits;
}
function responseOf(requests, ctx) {
    const waits = countedWaits(requests, ctx);
    const median = waits.length < RESPONSE_MIN_REQUESTS ? null : upperMedian(waits);
    if (median === null) {
        return { label: null, missing: `Sale cuando respondas ${String(RESPONSE_MIN_REQUESTS)} solicitudes en ${String(RESPONSE_WINDOW_DAYS)} días.` };
    }
    const step = RESPONSE_STEPS.find((s) => median <= s.upTo);
    return step === undefined
        ? { label: null, missing: "Sale cuando respondas el mismo día, en tus horas de atención." }
        : { label: step.label, missing: null };
}
/** A tiered count, or how many it lacks to pass the lowest tier: «Te faltan 4 …», «Te falta 1 …». */
function tieredOf(n, tiers, noun, lacking) {
    const label = tierLabel(n, tiers, noun);
    if (label !== null)
        return { label, missing: null };
    const short = (tiers[0] ?? 0) + 1 - n;
    return { label: null, missing: short === 1 ? `Te falta 1 ${lacking.one}.` : `Te faltan ${String(short)} ${lacking.many}.` };
}
/** The share of files that came back on another day, in integers; under 10 files, how many it lacks. */
function returningOf({ clients, returning }) {
    if (clients < RETURNING_MIN_CLIENTS) {
        const short = RETURNING_MIN_CLIENTS - clients;
        const window = `con trabajos en los últimos ${String(RETURNING_WINDOW_MONTHS)} meses`;
        return { label: null, missing: short === 1 ? `Te falta 1 cliente ${window}.` : `Te faltan ${String(short)} clientes ${window}.` };
    }
    const label = 10 * returning >= 7 * clients
        ? "7 de cada 10 clientes vuelven"
        : 2 * returning >= clients
            ? "La mitad de sus clientes vuelve"
            : 10 * returning >= 3 * clients
                ? "3 de cada 10 clientes vuelven"
                : null;
    return label === null ? { label: null, missing: "Sale cuando 3 de cada 10 de tus clientes vuelvan." } : { label, missing: null };
}
/** What «¿Qué significa?» says under the verified signals, the same on the web and on the phone. */
export const SIGNALS_EXPLANATION = "VozQI las calcula con el trabajo real que este negocio lleva en la app. El negocio no las escribe ni las puede cambiar; sólo puede ocultarlas.";
const SIGNALS = {
    jobs: {
        title: "Trabajos terminados",
        // «Trabajos terminados» does not describe a bakery's sales; a distributor's
        // count, since every sale it delivers is a job (`sells`, jobs "all").
        offered: (ctx) => verticalOf(ctx.businessType).profile.jobs !== "none",
        value: (facts) => tieredOf(facts.jobs, JOB_TIERS, "trabajos terminados", {
            one: `trabajo terminado en los últimos ${String(JOBS_WINDOW_MONTHS)} meses`,
            many: `trabajos terminados en los últimos ${String(JOBS_WINDOW_MONTHS)} meses`,
        }),
    },
    response: {
        title: "Tiempo de respuesta",
        // Only a trade that takes requests (the E2) has anything to answer.
        offered: (ctx) => keepsLogbook(ctx.businessType),
        value: (facts, ctx) => responseOf(facts.requests, ctx),
    },
    invoices: {
        title: "Factura electrónica",
        offered: (ctx) => COUNTRIES[ctx.country].fiscalAuthority !== null,
        value: (facts) => facts.invoicing
            ? { label: "Emite factura electrónica", missing: null }
            : { label: null, missing: "Sale cuando emitas una factura electrónica real." },
    },
    clients: {
        title: "Clientes en la app",
        offered: (ctx) => clientFaceOf(ctx.businessType) === "equipment",
        value: (facts) => tieredOf(facts.clients, CLIENT_TIERS, "clientes siguen sus equipos en la app", {
            one: "cliente con la app",
            many: "clientes con la app",
        }),
    },
    returning: {
        title: "Clientes que vuelven",
        offered: (ctx) => verticalOf(ctx.businessType).profile.jobs !== "none",
        value: (facts) => returningOf(facts.returning),
    },
    trajectory: {
        title: TRAJECTORY_TITLE,
        // Any trade: the owner's own trajectory, from three relations.
        offered: () => true,
        available: (facts) => facts.trajectory.total >= TRAJECTORY_SIGNAL_FROM,
        explanation: TRAJECTORY_EXPLANATION,
        value: (facts) => ({ label: trajectorySignalLabel(facts.trajectory), missing: null }),
    },
};
/** The signals this business may ever show: by its trade and its country. */
export function offeredSignals(ctx) {
    return SIGNAL_IDS.filter((id) => SIGNALS[id].offered(ctx));
}
/** Offered by its trade and available by its facts: a row of the settings. */
function availableSignals(facts, ctx) {
    return offeredSignals(ctx).filter((id) => SIGNALS[id].available?.(facts) ?? true);
}
function isBornOff(id) {
    return BORN_OFF_SIGNAL_IDS.includes(id);
}
/** A born-off signal is on once switched on; any other, until switched off. */
function isOn(id, switches) {
    return isBornOff(id) ? switches.shown.includes(id) : !switches.hidden.includes(id);
}
/** What the page shows: offered, available, on, and past its threshold. */
export function profileSignalsOf(facts, ctx, switches) {
    const shown = [];
    for (const id of availableSignals(facts, ctx)) {
        if (!isOn(id, switches))
            continue;
        const { label } = SIGNALS[id].value(facts, ctx);
        if (label !== null)
            shown.push({ id, label });
    }
    return shown;
}
/** What the settings show: every offered and available signal, with today's value or what it lacks. */
export function signalSettingsOf(facts, ctx, switches) {
    return availableSignals(facts, ctx).map((id) => {
        const { label, missing } = SIGNALS[id].value(facts, ctx);
        return { id, title: SIGNALS[id].title, label, missing, hidden: !isOn(id, switches), explanation: SIGNALS[id].explanation ?? null };
    });
}
/** What «Guardar» sends from the settings' rows: each row on its own column. */
export function signalSwitchesOf(rows) {
    return {
        hiddenSignals: rows.filter((row) => row.hidden && !isBornOff(row.id)).map((row) => row.id),
        shownSignals: rows.filter((row) => !row.hidden && isBornOff(row.id)).map((row) => row.id),
    };
}
/** What «Perfil público» in Configuración says when the profile lacks the zone or the hours, on both clients. */
export const PROFILE_MISSING = {
    zone: "Tu perfil no dice dónde estás.",
    hours: "Tu perfil no dice cuándo atiendes.",
};
/**
 * Where the business is, as the profile says it: the corregimiento or the
 * locality, and the region, a place already said not said again. Never the
 * street -- many technicians work from home; it shows only with «Atiendo en
 * un local», and on its own field. In Panama, with the accents the DGI's
 * names lack («Río Abajo, Panamá»).
 */
export function profileZoneOf(address, country) {
    const said = (name) => (country === "PA" ? withPlaceAccents(name) : name);
    const place = address.neighborhood.trim() === "" ? address.locality : address.neighborhood;
    const zone = formatAddress({ ...EMPTY_ADDRESS, neighborhood: said(place), region: said(address.region) });
    return zone === "" ? null : zone;
}
/**
 * A place as a map looks it up, closed by the country once: a zone that
 * already ends with it («…, Panamá», or the DGI's «…, Panama») does not say it
 * twice, and no zone is the country alone -- never a zone read in another one.
 */
export function withCountry(zone, country) {
    const name = COUNTRIES[country].name;
    if (zone === null)
        return name;
    return foldForSearch(zone).endsWith(foldForSearch(name)) ? zone : `${zone}, ${name}`;
}
/**
 * What «Ubicar en el mapa» looks up when there is no pin yet: the whole
 * address, the street too -- the owner's own screen, never the public one --
 * or null with nothing written, and the map starts on the capital.
 */
export function pinSearchOf(address, country) {
    const line = address.line.trim();
    const zone = profileZoneOf(address, country);
    if (line === "" && zone === null)
        return null;
    const place = withCountry(zone, country);
    return line === "" ? place : `${line}, ${place}`;
}
/**
 * «UBICAR EN EL MAPA» ON THE PHONE: the web's own picker, at `/ubicar`, in
 * the WebView the app already ships -- no native map, no new dependency. The
 * page knows nothing of the business: the phone tells it where to start and
 * in which palette, and it only answers points (`{ lat, lng }`, posted to the
 * WebView each time the map stops).
 */
const PIN_PICKER_PATH = "/ubicar";
/** The page's address, relative to the web's origin: the saved pin wins over the address. */
export function pinPickerPath(start) {
    const from = start.pin !== null
        ? `lat=${String(start.pin.lat)}&lng=${String(start.pin.lng)}`
        : start.search !== null
            ? `q=${encodeURIComponent(start.search)}`
            : null;
    return `${PIN_PICKER_PATH}?${from === null ? "" : `${from}&`}tema=${start.theme}`;
}
/** What the page reads back; anything missing or broken is null -- a lost `lat` is never 0, the middle of the ocean. */
export function pinPickerStartOf(params) {
    const numberAt = (name) => {
        const raw = params.get(name);
        return raw === null || raw.trim() === "" ? null : Number(raw);
    };
    const pin = profilePinSchema.safeParse({ lat: numberAt("lat"), lng: numberAt("lng") });
    const search = params.get("q")?.trim() ?? "";
    const theme = params.get("tema");
    return {
        pin: pin.success ? pin.data : null,
        search: search === "" ? null : search,
        theme: theme === "light" || theme === "dark" ? theme : null,
    };
}
/** What the page posts to the WebView each time the map stops. */
export function pinMessageOf(pin) {
    return JSON.stringify(pin);
}
/** What the phone reads from it: a point, or null for anything else the WebView may say. */
export function pinFromMessage(data) {
    let raw;
    try {
        raw = JSON.parse(data);
    }
    catch {
        return null;
    }
    const pin = profilePinSchema.safeParse(raw);
    return pin.success ? pin.data : null;
}
const PIN_MAP_STATES = ["loading", "locating", "ready", "failed", "unavailable"];
/** What the page posts each time its map changes state, beside the points. */
export function pinStateMessageOf(state) {
    return JSON.stringify({ state });
}
/** The state the page told, or null for a point or anything else. */
export function pinStateFromMessage(data) {
    let raw;
    try {
        raw = JSON.parse(data);
    }
    catch {
        return null;
    }
    if (typeof raw !== "object" || raw === null || !("state" in raw))
        return null;
    const state = PIN_MAP_STATES.find((known) => known === raw.state);
    return state ?? null;
}
/** «Usar este punto» with no point under the pin yet: why, and what to do -- never a dead button. */
export function pinWaitMessageOf(state) {
    switch (state) {
        case "locating":
            return "Buscando tu dirección en el mapa; un momento.";
        case "failed":
            return "El mapa no cargó. Toca «Reintentar» en el mapa.";
        case "unavailable":
            return "El mapa no está disponible por ahora. Intenta más tarde.";
        case "loading":
        case "ready":
            return "Espera a que cargue el mapa.";
    }
}
/**
 * Whether a WebView address is the picker page on the web's origin -- where
 * the phone's sheet may stand and the only page it hears. Compared as text,
 * so a look-alike host (`vozqi.com.evil.example`) or path (`/ubicarx`) is not it.
 */
export function isPinPickerUrl(url, origin) {
    const page = `${origin}${PIN_PICKER_PATH}`;
    return url === page || url.startsWith(`${page}?`) || url.startsWith(`${page}#`);
}
/** The trade the profile names: its record's `tradeName` where it has one, the type's own name otherwise, and none off the list. */
export function profileTradeOf(businessType) {
    if (!isBusinessType(businessType))
        return null;
    const trade = verticalOf(businessType).profile.tradeName;
    return trade === undefined ? businessType : trade;
}
/** The schema.org type Google reads the profile as. */
export function profileSchemaTypeOf(businessType) {
    return verticalOf(businessType).profile.schemaType;
}
/** Whether this trade's jobs include the sales born delivered: the car wash's washes, charged on the spot. */
export function jobsIncludeCounterSales(businessType) {
    return verticalOf(businessType).profile.jobs === "all";
}
/** Whether «Perfil público» exists for this business: its family says. */
export function offersPublicProfile(businessType) {
    return FAMILIES[familyOf(businessType)].publicProfile;
}
/**
 * What «Horario de atención» says under its title, and when the week is empty.
 * Every business with a public profile keeps its hours, which the profile
 * shows; only a logbook trade's customers also ask for
 * service within them, so only there do the hours speak of asking.
 */
export function businessHoursWordsFor(businessType) {
    if (!keepsLogbook(businessType)) {
        return { hint: "Cuándo atiendes. Sale en tu perfil público.", empty: "Sin horario: tu perfil público no dice cuándo atiendes." };
    }
    const parties = wordsFor(businessType).parties.toLowerCase();
    return {
        hint: `Cuándo pueden pedirte servicio tus ${parties} desde la app.`,
        empty: `Sin horario: tus ${parties} pueden pedir cualquier día y hora.`,
    };
}
/**
 * Whether the first publish asks «¿Qué servicios muestras?» (spec 2026-10-20
 * §5): the owner's consent to put service prices online. Only the
 * profile's first publish, and it asks UNLESS the business is known to have no
 * active service -- `services` (the first page of its services, null while it
 * loads or when it failed) unknown asks, since skipping the step shares every
 * price. Counted from the page, not its `total`: the list holds paused services
 * too, and the step lists only the active ones, as the server shares.
 */
export function publishAsksServices(firstPublish, services) {
    if (!firstPublish)
        return false;
    if (services === null)
        return true;
    return services.nextCursor !== null || services.items.some((item) => item.active);
}
/**
 * What `POST /shop/publish` carries: on the first publish the
 * services left unticked -- `[]` shares every active one; on a republish
 * nothing, which shares none (a profile published before keeps what it shared).
 */
export function publishExceptServices(firstPublish, unticked) {
    return firstPublish ? (unticked ?? []) : undefined;
}
/**
 * Why «Publicar» in «¿Qué servicios muestras?» has not published yet, or null.
 * `early`: pressed before the first page was on screen. `loadingRest`: pressed
 * with pages still unread, which load before it publishes -- the server shares
 * every service not left out, so one never seen would go out with its price.
 */
export function publishServicesNotice(state) {
    if (state.early && !state.loaded) {
        return state.failed ? "Reintenta cargar tus servicios para elegir cuáles salen." : "Espera a que carguen tus servicios.";
    }
    if (!state.loadingRest)
        return null;
    if (state.failed)
        return "Pulsa «Ver más» para cargar el resto de tus servicios y elegir cuáles salen.";
    return state.hasNext ? "Cargando el resto de tus servicios…" : "Ya están todos tus servicios: revisa los de abajo y pulsa «Publicar».";
}
/** The query `/c/<token>` reads to ask for the request before the account. */
export const PROFILE_FROM_PARAM = "desde";
export const PROFILE_FROM_VALUE = "perfil";
/** «Pedir servicio»: the poster's own page, told it comes from the profile. */
export function profileRequestHref(token) {
    return `/c/${token}?${PROFILE_FROM_PARAM}=${PROFILE_FROM_VALUE}`;
}
/** The profile offers the trajectory signal from this many relations. */
const TRAJECTORY_SIGNAL_FROM = 3;
function businesses(n) {
    return `${n} ${n === 1 ? "negocio" : "negocios"}`;
}
/** [P13] The profile's line, with exact numbers; null under three relations. */
export function trajectorySignalLabel(counts) {
    if (counts.total < TRAJECTORY_SIGNAL_FROM)
        return null;
    const median = counts.medianYears === null ? "" : ` · con la mitad, más de ${counts.medianYears} ${counts.medianYears === 1 ? "año" : "años"}`;
    const head = counts.live > 0
        ? `Trabaja con ${businesses(counts.live)} en VozQI · ha trabajado con ${counts.total}`
        : `Ha trabajado con ${businesses(counts.total)} en VozQI`;
    return `${head}${median}`;
}
/** «En tu perfil» and «Elegir qué sale», word for word on the web and the phone. */
export const PROFILE_ITEMS_COPY = {
    /** The section's title, and the catalogue's tag on a shared item while the profile is published. */
    title: "En tu perfil",
    choose: "Elegir qué sale",
    none: "No tienes servicios ni productos activos a la venta.",
    hint: "Sólo sale lo que eliges en tu catálogo.",
    show: "Mostrar en tu perfil",
    hide: "Quitar de tu perfil",
};
/** The catalogue's «Perfil» filter (`profile=`): «Todos» is no filter at all. */
export const PROFILE_FILTERS = ["in", "out"];
export const PROFILE_FILTER_LABELS = { in: "Publicados en tu perfil", out: "No publicados en tu perfil" };
/** What may be chosen for the profile in the catalogue: what it could list -- active, and for sale. */
export function mayShowOnProfile(item) {
    return item.active && sells(natureOf(item));
}
/** Every loaded item that may be chosen is ticked: «Elegir los N que coinciden» is offered then. */
export function allLoadedChosen(items, selected) {
    const choosable = items.filter(mayShowOnProfile);
    return choosable.length > 0 && choosable.every((item) => selected.has(item.id));
}
/** How many are chosen in the catalogue: the ticked rows, or every match once «Elegir los N» was pressed. */
export function profileChosenLabel(count, allMatching) {
    return allMatching ? `Los ${count} elegidos` : `${count} ${count === 1 ? "elegido" : "elegidos"}`;
}
/** The offer to widen a whole ticked page to every match. */
export function profileChooseAllLabel(total) {
    return `Elegir los ${total} que coinciden`;
}
/** What «Mostrar en tu perfil» and «Quitar de tu perfil» say once done, from the server's `changed`. */
export function profileItemsDoneMessage(show, changed) {
    if (show)
        return `Listo: ${changed} ${changed === 1 ? "sale" : "salen"} en tu perfil`;
    return `Listo: ${changed} ya no ${changed === 1 ? "sale" : "salen"} en tu perfil`;
}
/**
 * «Elegir qué sale»'s ticks as they are moved: each item's wish (`true`,
 * show it) recorded the moment it changes, so a tick made under one search
 * survives the next; one back to what is shared is forgotten. Several at once
 * for «Marcar los N que ves».
 */
export function tickProfileItems(changes, items, ticked) {
    const next = new Map(changes);
    for (const item of items) {
        if (ticked === (item.shared === true))
            next.delete(item.id);
        else
            next.set(item.id, ticked);
    }
    return next;
}
/** Whether the item reads ticked: the owner's change, or what is shared. */
export function profileItemTicked(changes, item) {
    return changes.get(item.id) ?? item.shared === true;
}
export function profileItemsChange(changes) {
    const change = { share: [], unshare: [] };
    changes.forEach((show, id) => (show ? change.share : change.unshare).push(id));
    return change;
}
/** The toggle above the list: what it does to the items loaded under this search. */
export function profileTickSeenLabel(seen, allTicked) {
    return `${allTicked ? "Desmarcar" : "Marcar"} ${seen === 1 ? "el 1 que ves" : `los ${seen} que ves`}`;
}
/** «En tu perfil»'s line: «4 servicios y 0 productos». */
export function profileShownLabel(shown) {
    const services = `${shown.services} ${shown.services === 1 ? "servicio" : "servicios"}`;
    const products = `${shown.products} ${shown.products === 1 ? "producto" : "productos"}`;
    return `${services} y ${products}`;
}
